import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Code2, Plus, RefreshCw, Shield, X, Pencil, Trash2 } from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { getSupabaseClient } from '../../../lib/supabaseClient';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import type { PolicyRuleType, ScopeKind } from '../../../types/prioritization/permissions';
import {
  buildPolicyPreview,
  CONDITION_FIELD_OPTIONS,
  createDefaultAuthoredPolicyConfig,
  describeCondition,
  generateConditionId,
  parseAuthoredPolicyConfig,
  setAuthoredPolicyCache,
  SUPPORTED_POLICY_ACTION_OPTIONS,
  type AuthoredPolicyCondition,
  type AuthoredPolicyConfig,
  type AuthoredPolicyRecord,
  type SupportedPolicyAction,
} from '../../../lib/authorization/authoredPolicies';
import { fetchWorkspacePolicies } from '../../../lib/authorization/authoredPolicyLoader';

type PolicyFormValues = {
  name: string;
  description: string;
  scopeKind: ScopeKind;
  scopeId: string;
  ruleType: PolicyRuleType;
  priority: number;
  enabled: boolean;
  ruleConfig: AuthoredPolicyConfig;
};

const SCOPE_OPTIONS: Array<{ value: ScopeKind; label: string }> = [
  { value: 'organization', label: 'Tenant (Organization)' },
  { value: 'resource', label: 'Workspace' },
  { value: 'space', label: 'Collaboration Space' },
  { value: 'department', label: 'Department' },
  { value: 'team', label: 'Team' },
  { value: 'project', label: 'Project' },
  { value: 'process', label: 'Process' },
  { value: 'session', label: 'Session' },
  { value: 'item', label: 'Item' },
];

const RULE_TYPE_OPTIONS: Array<{ value: PolicyRuleType; label: string; description: string }> = [
  { value: 'require_approval', label: 'Require Approval', description: 'Require approval before the action can proceed.' },
  { value: 'restrict_role', label: 'Restrict Role', description: 'Limit which roles can hold authority at this scope.' },
  { value: 'time_bound', label: 'Time Bound', description: 'Make a rule active only during a defined time window.' },
  { value: 'require_evidence', label: 'Require Evidence', description: 'Require supporting evidence before the action is allowed.' },
  { value: 'require_quorum', label: 'Require Quorum', description: 'Require a minimum number of participants for a decision.' },
];

function defaultScopeId(scopeKind: ScopeKind, tenantId: string | null, workspaceId: string | null): string {
  if (scopeKind === 'organization') return tenantId || '*';
  if (scopeKind === 'resource') return workspaceId || '*';
  return '*';
}

function parseCommaSeparatedList(value: string): string[] {
  return value
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function toDateTimeLocal(value?: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function fromDateTimeLocal(value: string): string | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
}

function getConditionFieldMeta(fieldId: AuthoredPolicyCondition['field']) {
  return CONDITION_FIELD_OPTIONS.find((option) => option.id === fieldId) ?? CONDITION_FIELD_OPTIONS[0];
}

function buildConfigFromBuilder(input: {
  ruleType: PolicyRuleType;
  selectedAction: SupportedPolicyAction;
  priority: number;
  conditions: AuthoredPolicyCondition[];
  approverRolesText: string;
  allowedRolesText: string;
  evidenceKey: string;
  minParticipants: number;
  startsAt: string;
  endsAt: string;
}): AuthoredPolicyConfig {
  const config: AuthoredPolicyConfig = {
    version: 1,
    targetActions: [input.selectedAction],
    conditions: input.conditions,
    params: {},
    ui: { priority: input.priority },
  };

  if (input.ruleType === 'require_approval') {
    config.params.approverRoles = parseCommaSeparatedList(input.approverRolesText);
  }
  if (input.ruleType === 'restrict_role') {
    config.params.allowedRoles = parseCommaSeparatedList(input.allowedRolesText);
  }
  if (input.ruleType === 'require_evidence') {
    config.params.evidenceKey = input.evidenceKey.trim() || 'evidenceProvided';
  }
  if (input.ruleType === 'require_quorum') {
    config.params.minParticipants = Number.isFinite(input.minParticipants) ? input.minParticipants : 0;
  }
  if (input.ruleType === 'time_bound') {
    config.params.startsAt = fromDateTimeLocal(input.startsAt);
    config.params.endsAt = fromDateTimeLocal(input.endsAt);
  }

  return config;
}

function ConditionEditor({
  condition,
  onChange,
  onRemove,
}: {
  condition: AuthoredPolicyCondition;
  onChange: (next: AuthoredPolicyCondition) => void;
  onRemove: () => void;
}) {
  const fieldMeta = getConditionFieldMeta(condition.field);
  const currentOperator = fieldMeta.operators.includes(condition.operator)
    ? condition.operator
    : fieldMeta.operators[0];

  return (
    <div className="grid gap-3 border border-[var(--rule)] bg-[var(--surface)] p-3 md:grid-cols-[1.4fr_1fr_1fr_auto]">
      <div>
        <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-[var(--text-ter)]">Field</label>
        <select
          value={condition.field}
          onChange={(event) =>
            onChange({
              ...condition,
              field: event.target.value as AuthoredPolicyCondition['field'],
              operator: getConditionFieldMeta(event.target.value as AuthoredPolicyCondition['field']).operators[0],
              value: undefined,
            })
          }
          className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full border border-[var(--rule)] px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
        >
          {CONDITION_FIELD_OPTIONS.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-[var(--text-ter)]">Operator</label>
        <select
          value={currentOperator}
          onChange={(event) =>
            onChange({
              ...condition,
              operator: event.target.value as AuthoredPolicyCondition['operator'],
            })
          }
          className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full border border-[var(--rule)] px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
        >
          {fieldMeta.operators.map((operator) => (
            <option key={operator} value={operator}>
              {operator.replaceAll('_', ' ')}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-[var(--text-ter)]">Value</label>
        {currentOperator === 'is_true' || currentOperator === 'is_false' ? (
          <div className="border border-dashed border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-ter)]">
            No value needed
          </div>
        ) : fieldMeta.valueType === 'boolean' ? (
          <select
            value={String(condition.value ?? true)}
            onChange={(event) =>
              onChange({
                ...condition,
                value: event.target.value === 'true',
              })
            }
            className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full border border-[var(--rule)] px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="true">true</option>
            <option value="false">false</option>
          </select>
        ) : (
          <input
            type={fieldMeta.valueType === 'number' ? 'number' : 'text'}
            value={Array.isArray(condition.value) ? condition.value.join(', ') : String(condition.value ?? '')}
            onChange={(event) =>
              onChange({
                ...condition,
                value:
                  fieldMeta.valueType === 'number'
                    ? Number(event.target.value)
                    : fieldMeta.valueType === 'string[]'
                      ? parseCommaSeparatedList(event.target.value)
                      : event.target.value,
              })
            }
            className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
            placeholder={fieldMeta.valueType === 'string[]' ? 'role-a, role-b' : 'Value'}
          />
        )}
      </div>

      <div className="flex items-end">
        <button
          type="button"
          onClick={onRemove}
          className="bg-[var(--bg-raised)] px-3 py-2 text-sm text-[var(--coral)] hover:bg-[var(--coral-dim)]"
        >
          Remove
        </button>
      </div>
    </div>
  );
}

function PolicyFormModal({
  title,
  initialValues,
  tenantId,
  workspaceId,
  onClose,
  onSubmit,
}: {
  title: string;
  initialValues?: Partial<AuthoredPolicyRecord>;
  tenantId: string | null;
  workspaceId: string | null;
  onClose: () => void;
  onSubmit: (values: PolicyFormValues) => Promise<void>;
}) {
  const initialConfig = parseAuthoredPolicyConfig(initialValues?.rule_config ?? {}, initialValues?.rule_type ?? 'require_approval');
  const [name, setName] = useState(initialValues?.name || '');
  const [description, setDescription] = useState(initialValues?.description || '');
  const [scopeKind, setScopeKind] = useState<ScopeKind>(initialValues?.scope_kind || 'resource');
  const [scopeId, setScopeId] = useState(
    initialValues?.scope_id || defaultScopeId(initialValues?.scope_kind || 'resource', tenantId, workspaceId)
  );
  const [ruleType, setRuleType] = useState<PolicyRuleType>(initialValues?.rule_type || 'require_approval');
  const [priority, setPriority] = useState(initialValues?.priority ?? 0);
  const [enabled, setEnabled] = useState(initialValues?.enabled ?? true);
  const [selectedAction, setSelectedAction] = useState<SupportedPolicyAction>(
    initialConfig.targetActions[0] ?? 'items.edit'
  );
  const [conditions, setConditions] = useState<AuthoredPolicyCondition[]>(
    initialConfig.conditions.length > 0 ? initialConfig.conditions : []
  );
  const [approverRolesText, setApproverRolesText] = useState((initialConfig.params.approverRoles ?? []).join(', '));
  const [allowedRolesText, setAllowedRolesText] = useState((initialConfig.params.allowedRoles ?? []).join(', '));
  const [evidenceKey, setEvidenceKey] = useState(initialConfig.params.evidenceKey ?? 'evidenceProvided');
  const [minParticipants, setMinParticipants] = useState(initialConfig.params.minParticipants ?? 2);
  const [startsAt, setStartsAt] = useState(toDateTimeLocal(initialConfig.params.startsAt));
  const [endsAt, setEndsAt] = useState(toDateTimeLocal(initialConfig.params.endsAt));
  const [showAdvancedEditor, setShowAdvancedEditor] = useState(false);
  const [advancedDirty, setAdvancedDirty] = useState(false);
  const [advancedJson, setAdvancedJson] = useState(JSON.stringify(initialConfig, null, 2));
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleScopeKindChange = (nextScopeKind: ScopeKind) => {
    setScopeKind(nextScopeKind);
    setScopeId(defaultScopeId(nextScopeKind, tenantId, workspaceId));
  };

  const builderConfig = useMemo(
    () =>
      buildConfigFromBuilder({
        ruleType,
        selectedAction,
        priority,
        conditions,
        approverRolesText,
        allowedRolesText,
        evidenceKey,
        minParticipants,
        startsAt,
        endsAt,
      }),
    [ruleType, selectedAction, priority, conditions, approverRolesText, allowedRolesText, evidenceKey, minParticipants, startsAt, endsAt]
  );

  useEffect(() => {
    if (!advancedDirty) {
      setAdvancedJson(JSON.stringify(builderConfig, null, 2));
    }
  }, [builderConfig, advancedDirty]);

  const advancedConfig = useMemo(() => {
    try {
      return parseAuthoredPolicyConfig(JSON.parse(advancedJson || '{}'), ruleType);
    } catch {
      return null;
    }
  }, [advancedJson, ruleType]);

  const activeConfig = advancedDirty && advancedConfig ? advancedConfig : builderConfig;
  const advancedError = advancedDirty && !advancedConfig ? 'Advanced JSON must be valid before you can save.' : null;

  const previewRecord = useMemo<AuthoredPolicyRecord>(
    () => ({
      id: initialValues?.id ?? 'draft-policy',
      name: name.trim() || 'Untitled policy',
      description: description.trim() || null,
      scope_kind: scopeKind,
      scope_id: scopeId.trim() || '*',
      rule_type: ruleType,
      rule_config: activeConfig,
      enabled,
      priority,
      created_by: initialValues?.created_by ?? null,
      created_at: initialValues?.created_at,
      updated_at: initialValues?.updated_at,
    }),
    [initialValues, name, description, scopeKind, scopeId, ruleType, activeConfig, enabled, priority]
  );

  const addCondition = () => {
    setConditions((existing) => [
      ...existing,
      {
        id: generateConditionId(),
        field: 'resource.workspaceId',
        operator: 'equals',
        value: workspaceId ?? '',
      },
    ]);
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setSubmitError('Name is required.');
      return;
    }

    if (advancedError) {
      setSubmitError(advancedError);
      return;
    }

    if (ruleType === 'restrict_role' && parseCommaSeparatedList(allowedRolesText).length === 0) {
      setSubmitError('Restrict role policies need at least one allowed role.');
      return;
    }

    if (ruleType === 'require_evidence' && !evidenceKey.trim()) {
      setSubmitError('Evidence policies need a field key to check.');
      return;
    }

    if (ruleType === 'require_quorum' && minParticipants < 1) {
      setSubmitError('Quorum policies need at least one required participant.');
      return;
    }

    if (ruleType === 'time_bound' && !startsAt && !endsAt) {
      setSubmitError('Time bound policies need a start, end, or both.');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      await onSubmit({
        name: name.trim(),
        description: description.trim(),
        scopeKind,
        scopeId: scopeId.trim() || '*',
        ruleType,
        priority,
        enabled,
        ruleConfig: activeConfig,
      });
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to save policy.');
      setSubmitting(false);
      return;
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 p-4">
      <div className="flex min-h-full items-center justify-center">
        <div className="flex max-h-[calc(100vh-2rem)] w-full max-w-3xl flex-col overflow-hidden bg-[var(--bg-raised)] p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-[var(--text-pri)]">{title}</h2>
          <button onClick={onClose} className="text-[var(--text-ter)] hover:text-[var(--text-sec)]">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 overflow-y-auto pr-1">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-[var(--text-sec)]">Name</label>
              <input
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                className="w-full border border-[var(--rule)] px-3 py-2 focus:ring-2 focus:ring-blue-500"
                placeholder="Policy name"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-[var(--text-sec)]">Rule Type</label>
              <select
                value={ruleType}
                onChange={(event) => {
                  const nextType = event.target.value as PolicyRuleType;
                  const defaults = createDefaultAuthoredPolicyConfig(nextType, selectedAction);
                  setRuleType(nextType);
                  if (nextType === 'require_approval' && !approverRolesText.trim()) {
                    setApproverRolesText((defaults.params.approverRoles ?? []).join(', '));
                  }
                  if (nextType === 'restrict_role' && !allowedRolesText.trim()) {
                    setAllowedRolesText((defaults.params.allowedRoles ?? []).join(', '));
                  }
                  if (nextType === 'require_evidence' && !evidenceKey.trim()) {
                    setEvidenceKey(defaults.params.evidenceKey ?? 'evidenceProvided');
                  }
                  if (nextType === 'require_quorum' && minParticipants < 1) {
                    setMinParticipants(defaults.params.minParticipants ?? 2);
                  }
                }}
                className="w-full border border-[var(--rule)] px-3 py-2 focus:ring-2 focus:ring-blue-500"
              >
                {RULE_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-[var(--text-sec)]">Description</label>
            <textarea
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              className="min-h-[80px] w-full border border-[var(--rule)] px-3 py-2 focus:ring-2 focus:ring-blue-500"
              placeholder="What this policy overrides or constrains"
            />
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-[var(--text-sec)]">Scope Kind</label>
              <select
                value={scopeKind}
                onChange={(event) => handleScopeKindChange(event.target.value as ScopeKind)}
                className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full border border-[var(--rule)] px-3 py-2 focus:ring-2 focus:ring-blue-500"
              >
                {SCOPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-[var(--text-sec)]">Scope ID</label>
              <input
                type="text"
                value={scopeId}
                onChange={(event) => setScopeId(event.target.value)}
                className="w-full border border-[var(--rule)] px-3 py-2 focus:ring-2 focus:ring-blue-500"
                placeholder="* or a specific scope id"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-[var(--text-sec)]">Priority</label>
              <input
                type="number"
                value={priority}
                onChange={(event) => setPriority(Number(event.target.value))}
                className="w-full border border-[var(--rule)] px-3 py-2 focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="border border-[var(--rule)] bg-[var(--surface)] p-3 text-sm text-[var(--text-sec)]">
            {RULE_TYPE_OPTIONS.find((option) => option.value === ruleType)?.description}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-[var(--text-sec)]">Target Action</label>
              <select
                value={selectedAction}
                onChange={(event) => setSelectedAction(event.target.value as SupportedPolicyAction)}
                className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full border border-[var(--rule)] px-3 py-2 focus:ring-2 focus:ring-blue-500"
              >
                {SUPPORTED_POLICY_ACTION_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="border border-[var(--slate)] bg-[var(--slate-dim)] p-3 text-sm text-[var(--text-sec)]">
              Changing type patterns and the four space-member acts are also checked by the database, so a rule on them holds wherever the change comes from.
            </div>
          </div>

          {ruleType === 'require_approval' && (
            <div>
              <label className="mb-1 block text-sm font-medium text-[var(--text-sec)]">Approver Roles</label>
              <input
                type="text"
                value={approverRolesText}
                onChange={(event) => setApproverRolesText(event.target.value)}
                className="w-full border border-[var(--rule)] px-3 py-2 focus:ring-2 focus:ring-blue-500"
                placeholder="workspace-admin, tenant-admin"
              />
              <p className="mt-1 text-xs text-[var(--text-ter)]">Comma-separated role ids. Matching roles can bypass the approval hold.</p>
            </div>
          )}

          {ruleType === 'restrict_role' && (
            <div>
              <label className="mb-1 block text-sm font-medium text-[var(--text-sec)]">Allowed Roles</label>
              <input
                type="text"
                value={allowedRolesText}
                onChange={(event) => setAllowedRolesText(event.target.value)}
                className="w-full border border-[var(--rule)] px-3 py-2 focus:ring-2 focus:ring-blue-500"
                placeholder="workspace-admin, tenant-admin"
              />
            </div>
          )}

          {ruleType === 'require_evidence' && (
            <div>
              <label className="mb-1 block text-sm font-medium text-[var(--text-sec)]">Evidence Key</label>
              <input
                type="text"
                value={evidenceKey}
                onChange={(event) => setEvidenceKey(event.target.value)}
                className="w-full border border-[var(--rule)] px-3 py-2 focus:ring-2 focus:ring-blue-500"
                placeholder="evidenceProvided"
              />
              <p className="mt-1 text-xs text-[var(--text-ter)]">Checked first on evaluation context extras, then on resource attributes.</p>
            </div>
          )}

          {ruleType === 'require_quorum' && (
            <div>
              <label className="mb-1 block text-sm font-medium text-[var(--text-sec)]">Minimum Participants</label>
              <input
                type="number"
                min={1}
                value={minParticipants}
                onChange={(event) => setMinParticipants(Number(event.target.value))}
                className="w-full border border-[var(--rule)] px-3 py-2 focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          {ruleType === 'time_bound' && (
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-[var(--text-sec)]">Starts At</label>
                <input
                  type="datetime-local"
                  value={startsAt}
                  onChange={(event) => setStartsAt(event.target.value)}
                  className="w-full border border-[var(--rule)] px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-[var(--text-sec)]">Ends At</label>
                <input
                  type="datetime-local"
                  value={endsAt}
                  onChange={(event) => setEndsAt(event.target.value)}
                  className="w-full border border-[var(--rule)] px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}

          <div className="space-y-3 border border-[var(--rule)] bg-[var(--bg-raised)] p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-[var(--text-pri)]">Conditions</h3>
                <p className="text-sm text-[var(--text-ter)]">Optional filters that decide when this policy should apply.</p>
              </div>
              <button
                type="button"
                onClick={addCondition}
                className="bg-[var(--hover-tint)] px-3 py-2 text-sm text-[var(--text-sec)] hover:bg-[var(--hover-tint)]"
              >
                Add Condition
              </button>
            </div>

            {conditions.length === 0 ? (
              <div className="border border-dashed border-[var(--rule)] px-4 py-6 text-sm text-[var(--text-ter)]">
                No extra conditions. This policy applies anywhere its scope and target action match.
              </div>
            ) : (
              <div className="space-y-3">
                {conditions.map((condition) => (
                  <ConditionEditor
                    key={condition.id}
                    condition={condition}
                    onChange={(next) =>
                      setConditions((existing) =>
                        existing.map((entry) => (entry.id === condition.id ? next : entry))
                      )
                    }
                    onRemove={() =>
                      setConditions((existing) => existing.filter((entry) => entry.id !== condition.id))
                    }
                  />
                ))}
              </div>
            )}
          </div>

          <div className="border border-[var(--green)] bg-[var(--green-dim)] p-4">
            <div className="mb-2 text-sm font-medium text-[var(--green)] dark:text-[var(--text-ter)]">Preview</div>
            <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)]">{buildPolicyPreview(previewRecord)}</p>
            {activeConfig.conditions.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {activeConfig.conditions.map((condition) => (
                  <span key={condition.id} className="rounded-full bg-[var(--bg-raised)] px-3 py-1 text-xs text-[var(--green)] dark:text-[var(--text-ter)]">
                    {describeCondition(condition)}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <button
              type="button"
              onClick={() => setShowAdvancedEditor((current) => !current)}
              className="flex items-center gap-2 text-sm font-medium text-[var(--text-sec)]"
            >
              <Code2 className="h-4 w-4" />
              Advanced JSON Editor
            </button>
            {showAdvancedEditor && (
              <div className="mt-3 space-y-3">
                <div className="border border-[var(--mustard)] bg-[var(--mustard-dim)] p-3 text-sm text-[var(--mustard)]">
                  Power-user mode. When you edit the JSON directly, the saved policy follows the JSON until you reset it from the builder.
                </div>
                <textarea
                  value={advancedJson}
                  onChange={(event) => {
                    setAdvancedDirty(true);
                    setAdvancedJson(event.target.value);
                  }}
                  className="min-h-[220px] w-full border border-[var(--rule)] px-3 py-2 font-mono text-sm focus:ring-2 focus:ring-blue-500"
                  spellCheck={false}
                />
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setAdvancedDirty(false);
                      setAdvancedJson(JSON.stringify(builderConfig, null, 2));
                    }}
                    className="bg-[var(--hover-tint)] px-3 py-2 text-sm text-[var(--text-sec)] hover:bg-[var(--hover-tint)]"
                  >
                    Reset JSON From Builder
                  </button>
                </div>
                {advancedError && (
                  <div className="border border-[var(--coral)] bg-[var(--coral-dim)] p-3 text-sm text-[var(--coral)]">
                    {advancedError}
                  </div>
                )}
              </div>
            )}
          </div>

          <label className="flex items-center gap-2 text-sm text-[var(--text-sec)]">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(event) => setEnabled(event.target.checked)}
              className="rounded border-[var(--rule)] text-[var(--text-ter)] focus:ring-blue-500"
            />
            Policy is enabled
          </label>

          {submitError && (
            <div className="border border-[var(--coral)] bg-[var(--coral-dim)] p-3 text-sm text-[var(--coral)]">
              {submitError}
            </div>
          )}
        </div>

        <div className="mt-6 flex shrink-0 justify-end gap-2 border-t border-[var(--rule)] pt-4">
          <button
            onClick={onClose}
            className="bg-[var(--hover-tint)] px-4 py-2 text-[var(--text-sec)] hover:bg-[var(--hover-tint)]"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-[var(--slate)] px-4 py-2 text-white hover:bg-[var(--slate)] disabled:bg-[var(--surface)]"
          >
            {submitting ? 'Saving...' : 'Save Policy'}
          </button>
        </div>
      </div>
      </div>
    </div>
  );
}

export default function Policies() {
  const currentRole = useUserStore((state) => state.currentRole);
  const switchRole = useUserStore((state) => state.switchRole);
  const canManagePolicies = useUserStore((state) => state.can('admin.policies'));
  const userId = useUserStore((state) => state.userId);
  const currentTenantId = useTenantStore((state) => state.currentTenantId);
  const currentWorkspaceId = useTenantStore((state) => state.currentWorkspaceId);

  const [policies, setPolicies] = useState<AuthoredPolicyRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createPrefill, setCreatePrefill] = useState<Partial<AuthoredPolicyRecord> | null>(null);
  const [editingPolicy, setEditingPolicy] = useState<AuthoredPolicyRecord | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep-link support: ?scope_kind=space&scope_id=<spaceId> opens the create modal
  // pre-filled for that scope.
  useEffect(() => {
    const scopeKind = searchParams.get('scope_kind') as ScopeKind | null;
    const scopeId = searchParams.get('scope_id');
    if (scopeKind && scopeId && !showCreateModal && !editingPolicy) {
      setCreatePrefill({ scope_kind: scopeKind, scope_id: scopeId });
      setShowCreateModal(true);
      // Clear params so it doesn't re-open if the user closes the modal
      const next = new URLSearchParams(searchParams);
      next.delete('scope_kind');
      next.delete('scope_id');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, showCreateModal, editingPolicy, setSearchParams]);

  // Policies live in the database (build contract §2.2): this page reads the
  // current workspace's rules and, after each change, refreshes the copy every
  // part of the app applies (authoredPolicyLoader fills it for everyone else).
  const loadPolicies = async () => {
    setLoading(true);
    setError(null);

    if (!currentWorkspaceId) {
      setPolicies([]);
      setLoading(false);
      return;
    }

    try {
      const loaded = await fetchWorkspacePolicies(currentWorkspaceId);
      setPolicies(loaded);
      setAuthoredPolicyCache(loaded, currentWorkspaceId);
    } catch (fetchError) {
      setError(
        `Could not load the policies: ${fetchError instanceof Error ? fetchError.message : 'unknown error'}`
      );
      setPolicies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canManagePolicies) {
      setLoading(false);
      return;
    }
    void loadPolicies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManagePolicies, currentWorkspaceId]);

  const filteredPolicies = useMemo(() => {
    if (!searchQuery) return policies;
    const query = searchQuery.toLowerCase();
    return policies.filter((policy) =>
      [policy.name, policy.description || '', policy.scope_kind, policy.scope_id, policy.rule_type, buildPolicyPreview(policy)]
        .join(' ')
        .toLowerCase()
        .includes(query)
    );
  }, [policies, searchQuery]);

  const savePolicy = async (values: PolicyFormValues, existingPolicy?: AuthoredPolicyRecord) => {
    if (!currentWorkspaceId) {
      throw new Error('Choose a workspace first — a policy belongs to one workspace.');
    }
    const supabase = getSupabaseClient();
    const persistedConfig: AuthoredPolicyConfig = {
      ...values.ruleConfig,
      ui: {
        ...values.ruleConfig.ui,
        priority: values.priority,
      },
    };
    const payload = {
      name: values.name,
      description: values.description || null,
      scope_kind: values.scopeKind,
      scope_id: values.scopeId || '*',
      rule_type: values.ruleType,
      rule_config: persistedConfig,
      enabled: values.enabled,
      priority: values.priority,
      workspace_id: currentWorkspaceId,
    };

    if (existingPolicy) {
      const { error: updateError } = await supabase
        .from('policies')
        .update({ ...payload, updated_at: new Date().toISOString(), updated_by: userId ?? null })
        .eq('id', existingPolicy.id);
      if (updateError) {
        throw new Error(`The policy was not saved: ${updateError.message}`);
      }
    } else {
      const { error: insertError } = await supabase.from('policies').insert({
        ...payload,
        ...(userId ? { created_by: userId } : {}),
      });
      if (insertError) {
        throw new Error(`The policy was not saved: ${insertError.message}`);
      }
    }

    await loadPolicies();
    setShowCreateModal(false);
    setEditingPolicy(null);
  };

  const deletePolicy = async (policyId: string) => {
    if (!confirm('Delete this policy?')) return;

    const supabase = getSupabaseClient();
    const { error: deleteError } = await supabase.from('policies').delete().eq('id', policyId);
    if (deleteError) {
      setError(`The policy was not deleted: ${deleteError.message}`);
      return;
    }
    await loadPolicies();
  };

  if (!canManagePolicies) {
    return (
      <div className="p-8">
        <div className="border border-[var(--coral)] bg-[var(--coral-dim)] p-4">
          <h2 className="mb-2 text-lg font-semibold text-[var(--coral)]">Access Denied</h2>
          <p className="mb-4 text-sm text-[var(--coral)]">
            You need the <code>admin.policies</code> permission to manage policy overrides.
          </p>
          <p className="mb-4 text-sm text-[var(--coral)]">
            Current role: {currentRole || 'none'}
          </p>
          <button
            onClick={() => switchRole('platform-admin')}
            className="bg-[var(--slate)] px-4 py-2 text-white hover:bg-[var(--slate)]"
          >
            Switch to Platform Admin
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Shield className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50"
        title="Policies"
        subtitle="Rules that hold an act back at a given scope — even for people whose role allows it. A policy never grants anything."
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => void loadPolicies()}
              disabled={loading}
              className="p-2 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-primary-600 text-white text-sm hover:bg-primary-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Create Policy
            </button>
          </div>
        }
      />

      <div className="grid gap-4 md:grid-cols-4">
        <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-4">
          <div className="text-sm text-[var(--text-ter)]">Total Policies</div>
          <div className="text-2xl font-bold text-[var(--text-pri)]">{policies.length}</div>
        </div>
        <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-4">
          <div className="text-sm text-[var(--text-ter)]">Enabled</div>
          <div className="text-2xl font-bold text-[var(--green)] dark:text-[var(--text-ter)]">{policies.filter((policy) => policy.enabled).length}</div>
        </div>
        <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-4">
          <div className="text-sm text-[var(--text-ter)]">Disabled</div>
          <div className="text-2xl font-bold text-[var(--text-ter)]">{policies.filter((policy) => !policy.enabled).length}</div>
        </div>
        <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-4">
          <div className="text-sm text-[var(--text-ter)]">Workspace Default Scope</div>
          <div className="truncate text-sm font-medium text-[var(--text-pri)]">{currentWorkspaceId || 'No workspace selected'}</div>
        </div>
      </div>

      <div className="border border-[var(--slate)] bg-[var(--slate-dim)] p-4 text-sm text-[var(--text-sec)]">
        Roles define baseline permissions. Policies are the explicit override layer for approvals, restrictions, evidence requirements, quorum rules, escalation, and audit constraints at a given scope.
      </div>

      <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-4">
        <label className="mb-2 block text-sm font-medium text-[var(--text-sec)]">Search policies</label>
        <input
          type="text"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          className="w-full border border-[var(--rule)] px-3 py-2 focus:ring-2 focus:ring-blue-500"
          placeholder="Search by name, scope, or rule type"
        />
      </div>

      {error && (
        <div className="border border-[var(--coral)] bg-[var(--coral-dim)] p-4 text-sm text-[var(--coral)]">
          {error}
        </div>
      )}

      <div className="border border-[var(--rule)] bg-[var(--bg-raised)] overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-[var(--text-ter)]">
            <RefreshCw className="mr-2 h-5 w-5 animate-spin" />
            Loading policies...
          </div>
        ) : filteredPolicies.length === 0 ? (
          <div className="py-12 text-center text-[var(--text-ter)]">
            {policies.length === 0
              ? 'No policies found. Create one to start defining scoped overrides.'
              : 'No policies match your search.'}
          </div>
        ) : (
          <div className="divide-y divide-[var(--rule)]">
            {filteredPolicies.map((policy) => (
              <div key={policy.id} className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-semibold text-[var(--text-pri)]">{policy.name}</h3>
                      <span className={`rounded px-2 py-0.5 text-xs font-medium ${policy.enabled ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' : 'bg-[var(--hover-tint)] text-[var(--text-sec)]'}`}>
                        {policy.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                      <span className="rounded bg-[var(--hover-tint)] px-2 py-0.5 text-xs text-[var(--text-sec)]">
                        Priority {policy.priority}
                      </span>
                    </div>
                    {policy.description && (
                      <p className="text-sm text-[var(--text-sec)]">{policy.description}</p>
                    )}
                    <p className="text-sm text-[var(--text-sec)]">{buildPolicyPreview(policy)}</p>
                    <div className="flex flex-wrap gap-2 text-xs text-[var(--text-ter)]">
                      <span className="rounded bg-[var(--hover-tint)] px-2 py-1">{policy.scope_kind}</span>
                      <span className="rounded bg-[var(--hover-tint)] px-2 py-1">{policy.scope_id}</span>
                      <span className="rounded bg-[var(--hover-tint)] px-2 py-1">{policy.rule_type}</span>
                      {parseAuthoredPolicyConfig(policy.rule_config, policy.rule_type).targetActions.map((action) => (
                        <span key={action} className="rounded bg-[var(--slate-dim)] px-2 py-1 text-[var(--text-sec)]">
                          {action}
                        </span>
                      ))}
                    </div>
                    {parseAuthoredPolicyConfig(policy.rule_config, policy.rule_type).conditions.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {parseAuthoredPolicyConfig(policy.rule_config, policy.rule_type).conditions.map((condition) => (
                          <span key={condition.id} className="rounded-full bg-[var(--hover-tint)] px-3 py-1 text-xs text-[var(--text-sec)]">
                            {describeCondition(condition)}
                          </span>
                        ))}
                      </div>
                    )}
                    <details className="bg-[var(--surface)] p-3">
                      <summary className="cursor-pointer text-xs font-medium text-[var(--text-sec)]">Advanced JSON</summary>
                      <pre className="mt-3 overflow-x-auto text-xs text-[var(--text-sec)]">
                        {JSON.stringify(policy.rule_config || {}, null, 2)}
                      </pre>
                    </details>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      onClick={() => setEditingPolicy(policy)}
                      className="p-2 text-[var(--text-ter)] hover:text-[var(--text-sec)]"
                      title="Edit policy"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => deletePolicy(policy.id)}
                      className="p-2 text-[var(--text-ter)] hover:text-[var(--coral)]"
                      title="Delete policy"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showCreateModal && (
        <PolicyFormModal
          title="Create Policy"
          initialValues={createPrefill ?? undefined}
          tenantId={currentTenantId}
          workspaceId={currentWorkspaceId}
          onClose={() => { setShowCreateModal(false); setCreatePrefill(null); }}
          onSubmit={(values) => savePolicy(values)}
        />
      )}

      {editingPolicy && (
        <PolicyFormModal
          title="Edit Policy"
          initialValues={editingPolicy}
          tenantId={currentTenantId}
          workspaceId={currentWorkspaceId}
          onClose={() => setEditingPolicy(null)}
          onSubmit={(values) => savePolicy(values, editingPolicy)}
        />
      )}
    </div>
  );
}
